import {Box, Typography} from "@mui/material";
import Button from "@mui/material/Button";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// @mui material components
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Custom styles for the Configurator
import ConfiguratorRoot from "./ConfiguratorRoot";

import {
  useMaterialUIController,
  setOpenConfigurator,
  setTransparentSidenav,
  setWhiteSidenav,
  setFixedNavbar,
  setSidenavColor,
} from "../../context";
import LanguageSwitcher from "../../components/LanguageSwitcher";

function Configurator() {
  const { t } = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const {
    openConfigurator,
    fixedNavbar,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [disabled, setDisabled] = useState(false);
  const sidenavColors = ["primary", "dark", "info", "success", "warning", "error"];

  // Use the useEffect hook to change the button state for the sidenav type based on window size.
  useEffect(() => {
    // A function that sets the disabled state of the buttons for the sidenav type.
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    // The event listener that's calling the handleDisabled function when resizing the window.
    window.addEventListener("resize", handleDisabled);

    // Call the handleDisabled function to set the state with the initial value.
    handleDisabled();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);
  const handleTransparentSidenav = () => {
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
  };
  const handleWhiteSidenav = () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
  };
  const handleDarkSidenav = () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
  };
  const handleFixedNavbar = () => setFixedNavbar(dispatch, !fixedNavbar);

  // sidenav type buttons styles
  const sidenavTypeButtonsStyles = ({
    functions: { pxToRem },
    palette: { white, dark, background },
    borders: { borderWidth },
  }: any) => ({
    height: pxToRem(39),
    background: darkMode ? background.default : white.main,
    color: `${darkMode ? white.main : dark.main} !important`,
    border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? background.default : white.main,
      color: `${darkMode ? white.main : dark.main} !important`,
      border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    },
  });

  // sidenav type active button styles
  const sidenavTypeActiveButtonStyles = ({
    functions: { pxToRem, linearGradient },
    palette: { white, gradients, background, dark },
  }: any) => ({
    height: pxToRem(39),
    background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
    color: `${darkMode ? dark.main : white.main} !important`,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
      color: `${darkMode ? dark.main : white.main} !important`,
    },
  });

  return (
    <>
      {/* Custom Backdrop */}
      {openConfigurator && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 1300,
            cursor: "pointer"
          }}
          onClick={handleCloseConfigurator}
        />
      )}
      
      {/* Configurator Panel */}
      <ConfiguratorRoot openConfigurator={openConfigurator}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
          pt={4}
          pb={0.5}
          px={3}
        >
          <Box>
            <Typography variant="h5">{t("configurator.title")}</Typography>
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              {t("configurator.subtitle")}
            </Typography>
          </Box>

          <Icon
            sx={({ typography: { size }, palette: { dark, white } }: any) => ({
              fontSize: `${size.lg} !important`,
              color: darkMode ? white.main : dark.main,
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
              transform: "translateY(5px)",
            })}
            onClick={handleCloseConfigurator}
          >
            close
          </Icon>
        </Box>

        <Divider />

        <Box pt={0.5} pb={3} px={3}>
          <Box>
            <Typography variant="h6">{t("configurator.sidenavColors")}</Typography>

            <Box mb={0.5}>
              {sidenavColors.map((color) => (
                <IconButton
                  key={color}
                  sx={({
                    borders: { borderWidth },
                    palette: { white, dark, background },
                    transitions,
                  }: any) => ({
                    width: "24px",
                    height: "24px",
                    padding: 0,
                    border: `${borderWidth[1]} solid ${darkMode ? background.default : white.main}`,
                    borderColor: () => {
                      let borderColorValue = sidenavColor === color && dark.main;

                      if (darkMode && sidenavColor === color) {
                        borderColorValue = white.main;
                      }

                      return borderColorValue;
                    },
                    transition: transitions.create("border-color", {
                      easing: transitions.easing.sharp,
                      duration: transitions.duration.shorter,
                    }),
                    backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }: any) =>
                      linearGradient(gradients[color].main, gradients[color].state),

                    "&:not(:last-child)": {
                      mr: 1,
                    },

                    "&:hover, &:focus, &:active": {
                      borderColor: darkMode ? white.main : dark.main,
                    },
                  })}
                  onClick={() => setSidenavColor(dispatch, color)}
                />
              ))}
            </Box>
          </Box>

          <Box mt={3} lineHeight={1}>
            <Typography variant="h6">{t("configurator.sidenavType")}</Typography>
            <Typography variant="button" sx={{ color: "text.primary" }}>
              {t("configurator.sidenavTypeHint")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                mt: 2,
                mr: 1,
              }}
            >
              <Button
                color="primary"
                variant="contained"
                onClick={handleDarkSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  !transparentSidenav && !whiteSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                {t("configurator.dark")}
              </Button>
              <Box sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleTransparentSidenav}
                  disabled={disabled}
                  fullWidth
                  sx={
                    transparentSidenav && !whiteSidenav
                      ? sidenavTypeActiveButtonStyles
                      : sidenavTypeButtonsStyles
                  }
                >
                  {t("configurator.transparent")}
                </Button>
              </Box>
              <Button
                color="primary"
                variant="contained"
                onClick={handleWhiteSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                {t("configurator.white")}
              </Button>
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={3}
            lineHeight={1}
          >
            <Typography variant="h6">{t("configurator.navbarFixed")}</Typography>

            <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
          </Box>
          <Divider />
          <Box mt={3} lineHeight={1}>
            <Typography variant="h6">{t("configurator.language")}</Typography>
            <Typography variant="button" sx={{ color: "text.primary" }}>
              {t("configurator.languageHint")}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <LanguageSwitcher fullWidth />
            </Box>
          </Box>
          <Divider />
        </Box>
      </ConfiguratorRoot>
    </>
  );
}

export default Configurator;
